import { useState, useEffect, useRef } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";
import { ButtonInput } from "../Inputs/ButtonInput";
import { PageMode } from "../../../models/PagedTable";
import ButtonGroup from "../ButtonGroup";

export interface IPaginationFilter {
  id: string;
  label: string;
  value: string | PageMode | number | boolean | any;
  type: "pageMode" | "additionalBody" | "searchBody";
  additionalBodyKey?: string; // Required if type is "additionalBody"
  default?: boolean;
}

export interface IPaginationWithFiltersProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalRows: number;
  itemLabel?: string; // e.g., "VenicOS" - defaults to "Rows"
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  filters?: IPaginationFilter[];
  activeFilter?: IPaginationFilter | null;
  onFilterChange?: (filter: IPaginationFilter | null) => void;
  isLoading?: boolean;
}

export default function PaginationWithFilters({
  currentPage,
  totalPages,
  pageSize,
  totalRows,
  itemLabel = "Rows",
  onPageChange,
  onPageSizeChange,
  filters = [],
  activeFilter = null,
  onFilterChange,
  isLoading = false,
}: IPaginationWithFiltersProps) {
  const { t } = useTranslation("common");
  const [pageInput, setPageInput] = useState<string>(currentPage.toString());
  const debouncedPageInput = useDebounce(pageInput, 500);
  const isUserTypingRef = useRef<boolean>(false);
  const lastExternalPageRef = useRef<number>(currentPage);

  // Update page input when currentPage changes externally (from buttons, etc.)
  // Only update if the change didn't come from user typing
  useEffect(() => {
    if (currentPage !== lastExternalPageRef.current && !isUserTypingRef.current) {
      lastExternalPageRef.current = currentPage;
      setPageInput(currentPage.toString());
    }
  }, [currentPage]);

  // Handle debounced page input - only trigger if user typed something
  useEffect(() => {
    // Skip if this change came from external navigation (buttons)
    if (!isUserTypingRef.current) {
      return;
    }

    const page = parseInt(debouncedPageInput, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
      lastExternalPageRef.current = page;
      isUserTypingRef.current = false; // Reset flag after applying change
    } else if (isNaN(page) || page < 1 || page > totalPages) {
      // Reset to current page if invalid
      setPageInput(currentPage.toString());
      isUserTypingRef.current = false;
    }
  }, [debouncedPageInput, totalPages, currentPage, onPageChange]);

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string for typing, or valid numbers
    if (value === "" || /^\d+$/.test(value)) {
      isUserTypingRef.current = true; // Mark that user is typing
      setPageInput(value);
    }
  };

  const handlePageInputBlur = () => {
    // On blur, ensure we have a valid value
    const page = parseInt(pageInput, 10);
    if (isNaN(page) || page < 1) {
      setPageInput("1");
      onPageChange(1);
      lastExternalPageRef.current = 1;
    } else if (page > totalPages) {
      setPageInput(totalPages.toString());
      onPageChange(totalPages);
      lastExternalPageRef.current = totalPages;
    } else if (page !== currentPage) {
      // Only update if different from current page
      onPageChange(page);
      lastExternalPageRef.current = page;
    } else {
      setPageInput(page.toString());
    }
    isUserTypingRef.current = false; // Reset flag on blur
  };

  const handlePrevious = () => {
    if (currentPage > 1 && !isLoading) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages && !isLoading) {
      onPageChange(currentPage + 1);
    }
  };

  const handleFilterChange = (id: string) => {
    if (isLoading) return;
    
    // Find the filter that matches this value
    const selectedFilter = filters.find(f => f.id === id);
    
    // Toggle: if clicking the same filter, deselect it; otherwise select the new one
    if (activeFilter?.id === selectedFilter?.id) {
      onFilterChange?.(null);
    } else {
      onFilterChange?.(selectedFilter || null);
    }
  };

  return (
    <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-3 md:gap-4 bg-gray-50 p-2 rounded-lg border border-gray-300">
      {/* Top Row (Mobile) / Left Section (Desktop) - Filters */}
      {filters.length > 0 ? (
        <div className="w-full md:w-auto">
          <ButtonGroup
            options={filters.map(filter => ({
              id: filter.id,
              label: filter.label,
              value: filter.id, // <-- stable
            }))}
            selectedIndex={filters.findIndex(f => f.id === activeFilter?.id)}
            onChange={handleFilterChange}
            disabled={isLoading}
            size="md"
            wrap
          />
        </div>
      ) : <div></div>}

      {/* Second Row (Mobile) / Middle & Right (Desktop) - Count and Pagination Controls */}
      <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4 w-full md:w-auto md:ml-auto">
        {/* Pagination Controls - First on mobile, last on desktop */}
        <div className="flex items-center gap-2 flex-wrap justify-center md:justify-end w-full md:w-auto">
          {/* Previous Button */}
          <ButtonInput
            label="<"
            isSubmit={false}
            onClick={handlePrevious}
            disabled={currentPage <= 1 || isLoading}
            classes="text-xl !py-1"
          />

          {/* Next Button */}
          <ButtonInput
            label=">"
            isSubmit={false}
            onClick={handleNext}
            disabled={currentPage >= totalPages || isLoading}
            classes="text-xl !py-1"
          />

          {/* Page Info */}
          <span className="flex items-center gap-1 text-sm text-gray-700 whitespace-nowrap">
            <div>{t("Page")}</div>
            <strong>
              {currentPage} {t("of")} {totalPages}
            </strong>
          </span>

          {/* Go to page input */}
          <span className="flex items-center gap-1 text-sm text-gray-700 whitespace-nowrap">
            {t("Go to page:")}
            <input
              type="text"
              value={pageInput}
              onChange={handlePageInputChange}
              onBlur={handlePageInputBlur}
              disabled={isLoading}
              className={`w-16 text-center text-gray-900 rounded-lg border-1 border-gray-300 px-2 py-1 text-sm ${
                isLoading ? "bg-gray-300 cursor-not-allowed" : ""
              }`}
              placeholder="1"
            />
          </span>

          {/* Show X dropdown */}
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={isLoading}
            className={`border-1 border-gray-300 rounded-lg bg-white px-2 py-1 text-sm w-32 ${
              isLoading ? "bg-gray-300 cursor-not-allowed" : ""
            }`}
          >
            {[10, 30, 50, 100, 200, 1000].map((size) => (
              <option key={size} value={size}>
                {t("Show {{count}}", { count: size })}
              </option>
            ))}
          </select>
        </div>

        {/* Item Count - Second on mobile, first on desktop */}
        <div className="text-center text-sm text-gray-700 md:order-first">
          {totalRows} {t(itemLabel)}
        </div>
      </div>
    </div>
  );
}
